import React, { useState } from 'react';
import {
  Grid,
  Typography,
  Icon,
  Stack,
  Icons,
} from '@nextiva/next-ui-components';
import { Avatar } from '@nextiva/next-ui-containers';
import { DATE_FORMATS, formatDate } from 'utils/date-helpers';
import { RegistrationInProgressProps } from 'interface/WizardV3';
import { getSubmissionDate } from 'utils/getSubmissionDate';
import { useGetUserInfoAvatar } from 'hooks/useGetUserInfoAvatar/useGetUserInfoAvatar';
import { CompanyTypeCell } from '../CompanyTypeCell';
import { useWizardContextV3 } from '../CampaignDialog/WizardContextV3/WizardContextV3';
import { ButtonDelete, ButtonStyled } from '../MessagingNew.styled';
import { DeleteRegistrationDialog } from '../CampaignDialog/CustomDialog/CustomDialog';
import { useDeleteEnablementInfo } from '../CampaignDialog/WizardContextV3/useDeleteEnablementInfo';

const dataTestId = 'PlatformAdminUI-Messaging-RegistrationInProgress';
export function RegistrationInProgress({
  handleOpenComplianceFeesDialog,
  smsCampaignData,
}: RegistrationInProgressProps) {
  const { enablementInfo, handleResetForm } = useWizardContextV3();
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const deleteEnablementInfo = useDeleteEnablementInfo();
  const { userInfo, isNotLoading } = useGetUserInfoAvatar(enablementInfo);
  const submissionDate = getSubmissionDate({ smsCampaignData, enablementInfo });

  return (
    <Grid container rowSpacing={3} data-testid={dataTestId}>
      <Grid container xs={12}>
        <Typography variant="subtitle2" color="#0B1029">
          Registration in-progress
        </Typography>
      </Grid>
      {isNotLoading && (
        <Grid container item columnSpacing={6} columns={15}>
          <Grid item xs={3}>
            <Typography
              variant="body2"
              color="#0B1029"
              fontWeight={800}
              marginBottom="8px"
            >
              Status:
            </Typography>
            <Stack direction="row" alignItems="center" gap={1}>
              <Icon
                icon={Icons.pauseCircle}
                size={16}
                sx={{ color: '#7A7D8C' }}
              />
              <Typography variant="body2" color="#0B1029">
                Incomplete
              </Typography>
            </Stack>
          </Grid>
          <CompanyTypeCell
            companyType={enablementInfo?.companyType}
            isApproved={false}
          />
          <Grid item xs={3}>
            <Typography
              variant="body2"
              color="#0B1029"
              fontWeight={800}
              marginBottom="8px"
            >
              Edited by:
            </Typography>
            <Stack direction="row" alignItems="center" gap={1}>
              <Avatar
                contact={userInfo}
                disablePresence
                avatarProps={{
                  size: 24,
                }}
              />
              <Typography variant="body2" color="#005FEC">
                {`${userInfo.firstName} ${userInfo.lastName}`}
              </Typography>
            </Stack>
          </Grid>
          <Grid item xs={3}>
            <Typography
              variant="body2"
              color="#0B1029"
              fontWeight={800}
              marginBottom="8px"
            >
              Last edited:
            </Typography>
            <Typography variant="body2" color="#0B1029">
              {formatDate(submissionDate, DATE_FORMATS.SHORT)}
            </Typography>
          </Grid>
        </Grid>
      )}
      {isNotLoading && (
        <Grid container item columnSpacing={2} alignItems="center">
          <Grid item>
            <ButtonStyled
              color="primary"
              variant="contained"
              onClick={handleOpenComplianceFeesDialog}
              data-testid={`${dataTestId}-ResumeButton`}
            >
              Resume registration
            </ButtonStyled>
          </Grid>
          <Grid item>
            <ButtonDelete
              color="error"
              variant="text"
              onClick={() => {
                setOpenDeleteDialog(true);
              }}
              data-testid={`${dataTestId}-DeleteButton`}
            >
              Delete
            </ButtonDelete>
          </Grid>
          <DeleteRegistrationDialog
            handleAccept={() => {
              deleteEnablementInfo.mutate(null, {
                onSuccess: handleResetForm,
              });
              setOpenDeleteDialog(false);
            }}
            handleCancel={() => {
              setOpenDeleteDialog(false);
            }}
            openDialog={openDeleteDialog}
            data-testid={`${dataTestId}-DeleteRegistrationDialog`}
          />
        </Grid>
      )}
    </Grid>
  );
}
